/**
 * O que faz: lista do dia do painel "Leads para retomar hoje", com botões de troca por grupo, a conversa do lead
 *   dentro do modal e um atalho para o chat principal.
 * Onde usa: CartaoRetomadaDashboard (Dashboard), ao clicar em um dos quatro grupos.
 * Conexões: useListaRetomada, regras de features/retomada, Dialog compartilhado e
 *   DashboardLeadConversation (carregado só quando um lead é escolhido).
 * Tabelas: nenhuma direta; a lista vem da RPC get_dashboard_retomada_lista_v1 e a conversa
 *   usa as mesmas consultas do chat (chat_mensagens e leads).
 * Data: 22/10/2026 (horários e faixa de retomado em 23/10/2026).
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, Clock, ExternalLink, Info, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CHAVE_RETOMADA, useListaRetomada } from '@/hooks/dashboard/useRetomadaDashboard';
import {
  APRESENTACAO_GRUPOS,
  GRUPOS_RETOMADA,
  descreverParada,
  descreverSituacao,
  formatarHorario,
  type GrupoRetomada,
} from '@/features/retomada/regras';
import type { ItemRetomada } from '@/services/retomadaService';
import { cn } from '@/lib/utils';

// O histórico e o compositor do chat não entram no pacote necessário para abrir a lista.
const DashboardLeadConversation = lazy(() => import('./DashboardLeadConversation').then((modulo) => ({ default: modulo.DashboardLeadConversation })));

interface RetomadaListaModalProps {
  aberto: boolean;
  grupoInicial: GrupoRetomada;
  onFechar: () => void;
  clinicaId?: string | null;
  adminMode?: boolean;
}

const CLASSE_SELO_SITUACAO = {
  retomado: 'bg-success-subtle text-success-text',
  lead_voltou: 'bg-info-subtle text-info-text',
} as const;

// Faixa na lateral do item: quem já foi resolvido hoje se destaca ao bater o olho, sem esconder da lista.
// A faixa transparente no pendente mantém todos os itens alinhados.
const CLASSE_FAIXA_SITUACAO = {
  pendente: 'border-l-transparent',
  retomado: 'border-l-success-text',
  lead_voltou: 'border-l-info-text',
} as const;

/** Texto do selo com horário quando existe ("Retomado hoje às 10:15"); pendente não tem selo. */
function textoSelo(item: ItemRetomada, dia: string): string | null {
  const selo = descreverSituacao(item.situacao);
  if (!selo) return null;
  const horario = item.retomado_em ? formatarHorario(item.retomado_em, dia) : '';
  // descreverSituacao já diz "hoje"; aqui entra só a hora para não repetir a palavra.
  return horario.startsWith('hoje ') ? `${selo} ${horario.slice('hoje '.length)}` : selo;
}

export function RetomadaListaModal({ aberto, grupoInicial, onFechar, clinicaId, adminMode = false }: RetomadaListaModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const consulta = useListaRetomada(clinicaId, aberto);
  const [grupo, setGrupo] = useState<GrupoRetomada>(grupoInicial);
  const [selecionado, setSelecionado] = useState<ItemRetomada | null>(null);
  // A ficha do lead começa recolhida, igual ao modal de contatos do Dashboard.
  const [fichaAberta, setFichaAberta] = useState(false);
  // Marca se alguma conversa foi aberta nesta abertura do modal; só então os números podem ter mudado.
  const conversaAberta = useRef(false);

  // Cada abertura começa no grupo clicado no cartão e sem conversa aberta.
  useEffect(() => {
    if (!aberto) return;
    setGrupo(grupoInicial);
    setSelecionado(null);
    // A ficha também volta recolhida: uma abertura nova não herda o painel aberto da anterior.
    setFichaAberta(false);
    conversaAberta.current = false;
  }, [aberto, grupoInicial]);

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data]);
  const itensDoGrupo = useMemo(() => itens.filter((item) => item.grupo === grupo), [itens, grupo]);
  const pendentesPorGrupo = useMemo(() => {
    const contagem: Record<GrupoRetomada, number> = { esperando_resposta: 0, parou_ontem: 0, parou_2_3_dias: 0, parou_4_7_dias: 0 };
    for (const item of itens) if (item.situacao === 'pendente') contagem[item.grupo] += 1;
    return contagem;
  }, [itens]);

  const fechar = () => {
    // Só quem abriu uma conversa pode ter respondido um lead e mudado os números do cartão.
    // Apenas olhar a lista não altera nada, e cada recarga das consultas percorre 8 dias de mensagens
    // da clínica: invalidar sempre jogaria fora o cache de 5 minutos à toa.
    if (conversaAberta.current) void queryClient.invalidateQueries({ queryKey: [CHAVE_RETOMADA] });
    // Limpa a conversa e a ficha só depois de decidir a invalidação (que olha o ref, não o estado): assim
    // o Dialog não fica com um lead antigo montado durante a animação de saída nem reabre com a ficha aberta.
    setSelecionado(null);
    setFichaAberta(false);
    onFechar();
  };

  const trocarGrupo = (novoGrupo: GrupoRetomada) => {
    setGrupo(novoGrupo);
    setSelecionado(null);
  };

  const abrirChatPrincipal = () => {
    if (!selecionado) return;
    const parametros = new URLSearchParams({ leadId: selecionado.id });
    // Em modo admin o chat precisa saber qual clínica está sendo analisada.
    if (adminMode && clinicaId) parametros.set('adminClinicId', clinicaId);
    fechar();
    navigate(`/chat?${parametros.toString()}`);
  };

  const total = consulta.data?.total ?? 0;
  const limitada = !!consulta.data && total > itens.length;

  return (
    <Dialog open={aberto} onOpenChange={(estaAberto) => { if (!estaAberto) fechar(); }}>
      <DialogContent className="flex h-[94dvh] w-[calc(100vw-1rem)] max-w-6xl flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:w-[94vw]">
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 pr-12 text-left sm:px-5 sm:pr-12">
          <DialogTitle className="truncate text-base">Leads para retomar hoje</DialogTitle>
          <DialogDescription className="text-xs">
            {APRESENTACAO_GRUPOS[grupo].descricao} Escolha um lead para ver a conversa.
          </DialogDescription>
        </DialogHeader>

        <div role="group" aria-label="Grupos de retomada" className="flex shrink-0 gap-2 overflow-x-auto border-b border-border px-4 py-2">
          {GRUPOS_RETOMADA.map((chave) => (
            <button
              key={chave}
              type="button"
              aria-pressed={grupo === chave}
              onClick={() => trocarGrupo(chave)}
              className={cn(
                'inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                grupo === chave ? 'border-transparent bg-accent font-semibold text-accent-foreground' : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', APRESENTACAO_GRUPOS[chave].classePonto)} aria-hidden="true" />
              {APRESENTACAO_GRUPOS[chave].nome}
              <span className="tabular-nums">{pendentesPorGrupo[chave]}</span>
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Com um lead aberto, a lista recolhe no celular para a conversa caber. */}
          <section
            aria-label={APRESENTACAO_GRUPOS[grupo].nome}
            aria-busy={consulta.isLoading}
            className={cn('min-w-0 flex-1 flex-col', !selecionado ? 'flex' : fichaAberta ? 'hidden border-r border-border xl:flex xl:max-w-[17rem]' : 'hidden border-r border-border sm:flex sm:max-w-sm')}
          >
            {/* scroll-fade no próprio dono do scroll: indica mais itens sem estado nem evento de rolagem. */}
            <div className="scroll-fade min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {consulta.error ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 p-5 text-center">
                  <p role="alert" className="text-sm text-muted-foreground">
                    {consulta.error instanceof Error ? consulta.error.message : 'Não foi possível carregar os leads para retomar.'}
                  </p>
                  <Button variant="outline" className="min-h-11" onClick={() => void consulta.refetch()}>Tentar novamente</Button>
                </div>
              ) : consulta.isLoading ? (
                <p role="status" className="p-8 text-center text-sm text-muted-foreground">Carregando leads…</p>
              ) : itensDoGrupo.length === 0 ? (
                <p role="status" className="p-8 text-center text-sm text-muted-foreground">Nenhum lead neste grupo hoje.</p>
              ) : (
                // key pelo grupo: a lista nova entra com fade curto ao trocar de aba.
                <ul key={grupo} className="divide-y divide-border animate-in fade-in-0 duration-200 motion-reduce:animate-none">
                  {itensDoGrupo.map((item) => {
                    const dia = consulta.data?.dia ?? '';
                    const selo = textoSelo(item, dia);
                    const ultimaMensagem = formatarHorario(item.ultima_mensagem_em, dia);
                    const nome = item.nome || 'contato sem nome';
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            conversaAberta.current = true;
                            setSelecionado(item);
                          }}
                          // O selo é visual; o nome acessível repete a situação para o leitor de tela.
                          aria-label={selo ? `Abrir conversa de ${nome} (${selo.toLowerCase()})` : `Abrir conversa de ${nome}`}
                          aria-pressed={selecionado?.id === item.id}
                          data-situacao={item.situacao}
                          className={cn('flex w-full items-start gap-3 border-l-4 px-4 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none', CLASSE_FAIXA_SITUACAO[item.situacao], selecionado?.id === item.id && 'bg-accent')}
                        >
                          <div className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium text-foreground">{item.nome || 'Nome não informado'}</span>
                              {selo && item.situacao !== 'pendente' && (
                                <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', CLASSE_SELO_SITUACAO[item.situacao])}>
                                  {item.situacao === 'retomado' && <CheckCircle2 className="h-3 w-3" aria-hidden="true" />}
                                  {selo}
                                </span>
                              )}
                            </span>
                            <span className="mt-0.5 block text-xs text-muted-foreground">{descreverParada(item)}</span>
                            {ultimaMensagem && (
                              <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
                                Última mensagem {ultimaMensagem}
                              </span>
                            )}
                            <span className="mt-1 line-clamp-2 text-xs text-foreground/80">
                              {item.ultimo_autor === 'lead' ? 'Lead: ' : 'Clínica: '}{item.trecho}
                            </span>
                          </div>
                          <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {limitada && (
              <p role="status" className="shrink-0 border-t border-border px-4 py-2 text-xs text-muted-foreground">
                Mostrando os {itens.length} mais urgentes de {total}.
              </p>
            )}
          </section>

          {selecionado && (
            <section aria-label="Conversa selecionada" className="flex min-w-0 flex-1 flex-col bg-background">
              <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
                <Button variant="ghost" className="min-h-11 min-w-11 p-2" aria-label="Voltar à lista de leads" onClick={() => setSelecionado(null)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{selecionado.nome || 'Nome não informado'}</p>
                  <p className="truncate text-xs text-muted-foreground">{selecionado.telefone || 'Telefone não informado'}</p>
                </div>
                {!fichaAberta && (
                  <Button variant="ghost" className="min-h-11 min-w-11 gap-2" aria-label="Mostrar informações do lead" aria-expanded={fichaAberta} onClick={() => setFichaAberta(true)}>
                    <Info className="h-4 w-4" /><span className="hidden md:inline">Mostrar lead</span>
                  </Button>
                )}
                <Button variant="outline" className="min-h-11 min-w-11 gap-2" aria-label="Abrir conversa na aba de chat" onClick={abrirChatPrincipal}>
                  <ExternalLink className="h-4 w-4" /><span className="hidden md:inline">Abrir no chat</span>
                </Button>
              </header>
              <div className="min-h-0 flex-1">
                <Suspense fallback={<p role="status" className="p-5 text-sm text-muted-foreground">Abrindo conversa…</p>}>
                  <DashboardLeadConversation
                    key={selecionado.id}
                    lead={selecionado}
                    clinicaId={clinicaId}
                    isAdminMode={adminMode}
                    mostrarInfo={fichaAberta}
                    onFecharInfo={() => setFichaAberta(false)}
                  />
                </Suspense>
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
